import AxiosInstance from "../../shared/utils/axios";
import { getAuditFields } from "../../shared/utils/auditFields";

export interface TrialFilterUserOption {
  id: string;
  name: string;
}

export interface TrialFilterFieldOption {
  id: string;
  name: string;
}

export interface TrialFiltersApiResponse {
  crop: string[];
  country: string[];
  fields: TrialFilterFieldOption[];
  created_by: TrialFilterUserOption[];
  assignees: TrialFilterUserOption[];
}

export interface TrialListFilterParams {
  protocol_type?: string[];
  crop?: string[];
  execution_status?: string[];
  country?: string[];
  field_id?: string[];
  created_by?: string[];
  assignee_id?: string[];
  date_from?: string;
  date_to?: string;
}

/**
 * Appends array query params as repeated query params.
 * Example: crop=Corn&crop=Wheat
 */
const appendArrayParams = (
  params: URLSearchParams,
  key: string,
  values?: string[]
): void => {
  values?.forEach((value) => {
    if (value) {
      params.append(key, value);
    }
  });
};

/**
 * Fetches available filter options for trials.
 * @param orgId The ID of the organization.
 * @param farmId The ID of the farm/property.
 * @returns Available trial filter options.
 */
export const fetchTrialFilters = async (
  orgId: string,
  farmId: string
): Promise<TrialFiltersApiResponse> => {
  const params = new URLSearchParams({
    org_id: orgId,
    property_id: farmId,
  });

  const response = await AxiosInstance.get(
    `/trials/filters?${params.toString()}`
  );

  return response.data;
};

/**
 * Builds the filter query for the trials insights endpoints.
 *
 * These endpoints accept exactly the filters `GET /trials` accepts, so this
 * mirrors `fetchTrials`' filter block — deliberately as a separate copy rather
 * than a shared helper, to keep the working list request untouched. Pagination
 * is excluded: insights always aggregate the whole filtered set, never a page.
 *
 * Keep in step with `fetchTrials` when a filter is added on either side.
 */
export const buildTrialInsightsParams = (
  orgId: string,
  farmId: string,
  search: string = "",
  status: string = "",
  extraFilters: TrialListFilterParams = {}
): URLSearchParams => {
  const params = new URLSearchParams();

  params.append("org_id", orgId);

  if (farmId) {
    params.append("property_id", farmId);
  }

  params.append("search", search);

  if (status === "completed") {
    params.append("completion", "true");
  } else if (status === "incomplete") {
    params.append("completion", "false");
  }

  appendArrayParams(params, "protocol_type", extraFilters.protocol_type);
  appendArrayParams(params, "crop", extraFilters.crop);
  appendArrayParams(params, "execution_status", extraFilters.execution_status);
  appendArrayParams(params, "country", extraFilters.country);
  appendArrayParams(params, "field_id", extraFilters.field_id);
  appendArrayParams(params, "created_by", extraFilters.created_by);
  appendArrayParams(params, "assignee_id", extraFilters.assignee_id);

  if (extraFilters.date_from) {
    params.append("date_from", extraFilters.date_from);
  }

  if (extraFilters.date_to) {
    params.append("date_to", extraFilters.date_to);
  }

  return params;
};

/**
 * Fetches trials data based on the provided parameters.
 * @param orgId The ID of the organization.
 * @param farmId The ID of the farm.
 * @param page The page number for pagination.
 * @param limit The number of items per page.
 * @param search The search query.
 * @param status The legacy completion status filter.
 * @param extraFilters Additional filter params from the new filter drawer.
 * @returns trials data
 */
export const fetchTrials = async (
  orgId: string,
  farmId: string,
  page: number = 1,
  limit: number = 10,
  search: string = "",
  status: string = "",
  extraFilters: TrialListFilterParams = {}
): Promise<any> => {
  const params = new URLSearchParams();

  params.append("org_id", orgId);

  if (farmId) {
    params.append("property_id", farmId);
  }

  params.append("page", String(page));
  params.append("limit", String(limit));
  params.append("search", search);
  params.append("paginated", "true");

  if (status === "completed") {
    params.append("completion", "true");
  } else if (status === "incomplete") {
    params.append("completion", "false");
  }

  appendArrayParams(params, "protocol_type", extraFilters.protocol_type);
  appendArrayParams(params, "crop", extraFilters.crop);
  appendArrayParams(params, "execution_status", extraFilters.execution_status);
  appendArrayParams(params, "country", extraFilters.country);
  appendArrayParams(params, "field_id", extraFilters.field_id);
  appendArrayParams(params, "created_by", extraFilters.created_by);
  appendArrayParams(params, "assignee_id", extraFilters.assignee_id);

  if (extraFilters.date_from) {
    params.append("date_from", extraFilters.date_from);
  }

  if (extraFilters.date_to) {
    params.append("date_to", extraFilters.date_to);
  }

  const response = await AxiosInstance.get(`/trials?${params.toString()}`);

  return response.data;
};

/**
 * Fetches the latest details of a single trial.
 * @param trialId The ID of the trial to fetch.
 * @returns the trial details for the given trial ID.
 */
export const fetchTrialById = async (trialId: string): Promise<any> => {
  const response = await AxiosInstance.get(`/trial/${trialId}`);
  return response.data;
};

/**
 * Generates a report for the specified trial IDs and field IDs.
 * @param trialId The IDs of the trials for which the report is to be generated.
 * @param fieldIds The IDs of the fields for which the report is to be generated.
 * @returns A promise that resolves to an object containing the UUID and report ID of the generated report.
 */
export const generateReport = async (
  trialId: string[],
  fieldIds: string[]
): Promise<{ uuid: string; reportId: string }> => {
  const response = await AxiosInstance.post("/online_report/generate", {
    trial_ids: trialId,
    field_ids: fieldIds,
  });

  const uuid = response.data?.UUID || response.data?.uuid;
  const reportId = response.data?.report_id;

  if (!uuid || !reportId) {
    throw new Error("No report UUID or reportId received in response");
  }

  return { uuid, reportId };
};

/**
 * Deletes a trial with the specified trial ID.
 * @param trialId The ID of the trial to be deleted.
 */
export const deleteTrial = async (trialId: string): Promise<void> => {
  await AxiosInstance.delete(`/trial/${trialId}`);
};

/**
 * Toggles a trial's completion/activation state based on the provided trial ID.
 * Sets `completion` to `checked`, and `execution_status` to "activated" when
 * `checked` is true or back to "ready" when `checked` is false.
 * @param trialId The ID of the trial to be updated.
 * @param checked Indicates whether the trial is complete/activated.
 * @param userId Id of the logged-in user, sent as `updated_by`.
 */
export const markTrialComplete = async (
  trialId: string,
  checked: boolean,
  userId?: string
): Promise<any> => {
  const response = await AxiosInstance.patch(`/trial/${trialId}`, {
    completion: checked,
    execution_status: checked ? "activated" : "ready",
    ...getAuditFields("patch", userId),
  });

  return response.data;
};
